import { useCallback, useEffect, useState } from 'react';
import { getJson } from '../api/client';
import { Alert, Badge, Button, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno } from '../lib/format';
import { navigate } from '../router';
import styles from './screens.module.css';

/** Fatura da unidade do usuário, como `/api/me/faturas` a devolve. `subtotal` já inclui gás e encargos anteriores. */
export interface Fatura {
  id: number;
  mes: number;
  ano: number;
  subtotal: number;
  gas: number;
  encargosAnteriores: number;
  situacao: string;
  vencimento: string | null;
}
interface Demonstrativo {
  taxaCondominio: number;
  fundoReserva: number;
  fundoPintura: number;
  gas: number;
  encargosAnteriores: number;
  multa: number;
  juros: number;
  totalComEncargos: number;
  situacao: string;
  vencimento: string | null;
  dataPagamento: string | null;
}
interface DetalheFatura {
  demonstrativo: Demonstrativo;
  condominio?: { razaoSocial?: string | null };
  pix?: { copiaECola?: string | null };
}
interface FaturaAberta {
  fatura: Fatura;
  demo: Demonstrativo;
  pix: string | null;
  condominio: string | null;
}

const ROTULO_SITUACAO: Record<string, string> = {
  aberto: 'Em aberto',
  pago: 'Pago',
  atrasado: 'Atrasado',
  divergente: 'Divergente',
};

/** Situação da fatura em palavras; valor desconhecido aparece como veio da API. */
export function rotuloSituacao(situacao: string): string {
  return ROTULO_SITUACAO[situacao] ?? situacao;
}

/** Fatura pedida na URL (`/faturas?fatura=ID`, do "Baixar fatura" da Home); `0` quando ausente ou inválida. */
function faturaPedidaNaUrl(): number {
  const id = Number(new URLSearchParams(window.location.search).get('fatura'));
  return Number.isInteger(id) ? id : 0;
}

export function Faturas() {
  const [faturas, setFaturas] = useState<Fatura[] | null>(null);
  const [aberta, setAberta] = useState<FaturaAberta | null>(null);
  const [erro, setErro] = useState(false);

  const abrir = useCallback(async (f: Fatura) => {
    setErro(false);
    const data = await getJson<DetalheFatura>(
      `/api/demonstrativos/${encodeURIComponent(f.id)}/fatura`,
    );
    if (!data) {
      setErro(true);
      return;
    }
    setAberta({
      fatura: f,
      demo: data.demonstrativo,
      pix: data.pix?.copiaECola ?? null,
      condominio: data.condominio?.razaoSocial ?? null,
    });
  }, []);

  useEffect(() => {
    void getJson<{ faturas: Fatura[] }>('/api/me/faturas').then((d) => {
      const lista = d?.faturas ?? [];
      setFaturas(lista);
      const pedida = lista.find((f) => f.id === faturaPedidaNaUrl());
      if (pedida) void abrir(pedida);
    });
  }, [abrir]);

  function voltar() {
    setAberta(null);
    navigate('/faturas', { replace: true });
  }

  if (faturas === null) return <Spinner />;
  if (aberta) return <Detalhe item={aberta} onVoltar={voltar} />;
  if (faturas.length === 0)
    return (
      <EmptyState>
        Nenhuma fatura disponível ainda. Ela aparece quando o síndico fecha o mês.
      </EmptyState>
    );

  return (
    <div>
      {erro ? <Alert kind="danger">Não foi possível abrir a fatura. Tente novamente.</Alert> : null}
      <ul className={styles.list}>
        {faturas.map((f) => (
          <li key={f.id}>
            <button type="button" className={styles.listItem} onClick={() => void abrir(f)}>
              <span>{mesAno(f)}</span>
              <span style={{ display: 'inline-flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                <strong>{brl(f.subtotal)}</strong>
                <Badge tone={f.situacao === 'pago' ? 'success' : 'warning'}>
                  {rotuloSituacao(f.situacao)}
                </Badge>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Fatura para ler e guardar (D8, ADR-016): a visão é imprimível e o navegador gera o PDF.
 * Só o bloco `fatura-imprimivel` sai no papel (regras `@media print` em global.css).
 */
function Detalhe({ item, onVoltar }: { item: FaturaAberta; onVoltar: () => void }) {
  const { fatura, demo, pix, condominio } = item;
  const linhas: Array<[string, number]> = [
    ['Taxa de condomínio', demo.taxaCondominio],
    ['Fundo de reserva', demo.fundoReserva],
    ['Fundo de pintura', demo.fundoPintura],
    ['Gás', demo.gas],
  ];
  if (demo.encargosAnteriores > 0) linhas.push(['Encargos anteriores', demo.encargosAnteriores]);
  if (demo.multa > 0) linhas.push(['Multa (2%)', demo.multa]);
  if (demo.juros > 0) linhas.push(['Juros (1% a.m.)', demo.juros]);

  return (
    <div>
      <div className="fatura-imprimivel">
        {condominio ? <p className="muted">{condominio}</p> : null}
        <h2>Fatura {mesAno(fatura)}</h2>
        <dl className={styles.infoFatura}>
          <div>
            <dt>Vencimento</dt>
            <dd>{demo.vencimento ? dataBr(demo.vencimento) : '—'}</dd>
          </div>
          <div>
            <dt>Situação</dt>
            <dd>{rotuloSituacao(demo.situacao)}</dd>
          </div>
          {demo.dataPagamento ? (
            <div>
              <dt>Pagamento</dt>
              <dd>{dataBr(demo.dataPagamento)}</dd>
            </div>
          ) : null}
        </dl>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <tbody>
              {linhas.map(([rotulo, valor]) => (
                <tr key={rotulo}>
                  <th scope="row">{rotulo}</th>
                  <td className={styles.num}>{brl(valor)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={styles.totalRow}>
                <th scope="row">Total</th>
                <td className={styles.num}>{brl(demo.totalComEncargos)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {pix && demo.situacao !== 'pago' ? (
          <div className={styles.pix}>
            <strong>Pix copia-e-cola</strong>
            <code className={styles.pixCode}>{pix}</code>
            <Button
              className="no-print"
              variant="secondary"
              size="sm"
              onClick={() => navigator.clipboard?.writeText(pix).catch(() => {})}
            >
              Copiar código
            </Button>
          </div>
        ) : null}
      </div>
      <div className={`no-print ${styles.acoesFatura}`}>
        <Button onClick={() => window.print()}>Imprimir ou salvar em PDF</Button>
        <Button variant="ghost" onClick={onVoltar}>
          ← Voltar
        </Button>
      </div>
    </div>
  );
}
